import type React from "react";
import { useEffect, useState } from "react";

import { DatePicker, Select, Tooltip } from "antd";
import { utc } from "dayjs";
import moment, { Moment } from "moment";
import styled from "styled-components";

import { mBinSize } from "models/binSize";

const { unix } = moment;

const BinInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
`;

const BinInputLabel = styled.label`
  font-size: 12px;
  margin-bottom: 5px;
  font-weight: 500;
  color: #333;
`;

const StyledInput = styled.input`
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  width: 100%;
  &:disabled {
    background-color: #f0f0f0;
  }
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  &:disabled {
    background-color: #f0f0f0;
  }
  min-height: 34px;
`;

const StyledSelect = styled(Select)`
  width: 100%;
  &:disabled {
    background-color: #f0f0f0;
  }
`;

const BinSizeContainer = styled.div`
  display: flex;
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow: hidden;
  &:focus-within {
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

const BinSizeInput = styled(StyledInput)`
  flex: 0 0 40%;
  border: none;
  border-radius: 0;
  border-right: 1px solid #eee;
  &:focus {
    outline: none;
    box-shadow: none;
  }
`;

const BinSizeSelect = styled(StyledSelect)`
  flex: 1;
  border: none !important;
  box-shadow: none !important;
  .ant-select-selector {
    border: none !important;
    box-shadow: none !important;
  }
`;

interface DateBinSettingsProps {
  bin: mBinSize;
  handleLessThanChange: (lessThan: number) => void;
  handleGreaterThanChange: (greaterThan: number) => void;
  handleBinSizeChange: (binSize: number) => void;
  onBinKeyDown?: (
    evt: KeyboardEvent,
    binType: string,
    selectedQuantileType: string
  ) => void;
  canEditBin: boolean;
  selectedQuantileType: string;
}

export const YEAR_TIMESTAMP = 31536000; //365 days
export const MONTH_TIMESTAMP = 2592000;
const DAY_TIMESTAMP = 86400;

const DateBinSettings: React.FC<DateBinSettingsProps> = ({
  bin,
  handleLessThanChange,
  handleGreaterThanChange,
  handleBinSizeChange,
  canEditBin
}) => {
  const [lessThan, setLessThan] = useState<Moment | undefined>(undefined);
  const [greaterThan, setGreaterThan] = useState<Moment | undefined>(undefined);
  const [binSize, setBinSize] = useState<number>(1);
  const [timeStep, setTimeStep] = useState<"month" | "year">("month");

  useEffect(() => {
    if (!bin.LessThan) {
      setLessThan(undefined);
      return;
    } else {
      const date = unix(parseInt(bin.LessThan as string)).utc();
      setLessThan(date);
    }
  }, [bin.LessThan]);

  useEffect(() => {
    if (!bin.GreaterThan) {
      setGreaterThan(undefined);
      return;
    } else {
      const date = unix(parseInt(bin.GreaterThan as string)).utc();
      setGreaterThan(date);
    }
  }, [bin.GreaterThan]);

  useEffect(() => {
    const binSize = (bin.BinSize as number) / YEAR_TIMESTAMP;

    if (timeStep == "year") {
      setBinSize(binSize || 1);
    } else if (timeStep == "month") {
      setBinSize((bin.BinSize as number) / MONTH_TIMESTAMP || 1);
    } else if (timeStep == "day") {
      setBinSize((bin.BinSize as number) / DAY_TIMESTAMP);
    }
  }, [bin.BinSize, timeStep]);

  return (
    <>
      <Tooltip title="Less Than Date">
        <BinInputWrapper>
          <BinInputLabel htmlFor="less-than-date">Less than</BinInputLabel>
          <StyledDatePicker
            id="less-than-date"
            disabled={!canEditBin}
            value={lessThan}
            onChange={(date?: Moment) => {
              if (!date) {
                handleLessThanChange && handleLessThanChange(undefined);
                return;
              }
              const d = `${date.year()}-${date.month() + 1}-${date.date()}`;
              const ts = utc(d).unix();
              handleLessThanChange && handleLessThanChange(ts);
            }}
            format="YYYY-MM-DD"
          />
        </BinInputWrapper>
      </Tooltip>

      <Tooltip title="Bin Size">
        <BinInputWrapper>
          <BinInputLabel htmlFor="bin-size-input">Bin size</BinInputLabel>
          <BinSizeContainer>
            <BinSizeInput
              id="bin-size-input"
              type="number"
              min={1}
              max={50}
              step={1}
              disabled={!canEditBin}
              value={binSize}
              onChange={(evt: InputEvent) => {
                const number = parseInt(
                  (evt.target as unknown as { value: string }).value
                );
                if (timeStep == "year") {
                  handleBinSizeChange && handleBinSizeChange(number * YEAR_TIMESTAMP);
                } else if (timeStep == "month") {
                  handleBinSizeChange && handleBinSizeChange(number * MONTH_TIMESTAMP);
                }
              }}
            />
            <BinSizeSelect
              disabled={!canEditBin}
              value={timeStep}
              onChange={setTimeStep}
              options={[
                { value: "month", label: "Months" },
                { value: "year", label: "Years" }
              ]}
              dropdownStyle={{ minWidth: "120px" }}
            />
          </BinSizeContainer>
        </BinInputWrapper>
      </Tooltip>

      <Tooltip title="Greater Than Date">
        <BinInputWrapper>
          <BinInputLabel htmlFor="greater-than-date">Greater than</BinInputLabel>
          <StyledDatePicker
            id="greater-than-date"
            disabled={!canEditBin}
            value={greaterThan}
            onChange={(date: Moment | undefined) => {
              if (!date) {
                handleGreaterThanChange && handleGreaterThanChange(undefined);
                return;
              }
              const d = `${date.year()}-${date.month() + 1}-${date.date()}`;
              const ts = utc(d).unix();
              handleGreaterThanChange && handleGreaterThanChange(ts);
            }}
            format="YYYY-MM-DD"
          />
        </BinInputWrapper>
      </Tooltip>
    </>
  );
};

export default DateBinSettings;
